import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalculateIcon from "@mui/icons-material/Calculate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import "katex/dist/katex.min.css";
import { InlineMath, BlockMath } from "react-katex";
import { getApiUrl } from "../config";
import { evaluate } from "mathjs";
import Fraction from "fraction.js";
import { toast } from "react-toastify";

// ── keyframes ──
const fadeUp = keyframes`from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}`;
const slideInL = keyframes`from{opacity:0;transform:translateX(-100%)}to{opacity:1;transform:translateX(0)}`;
const urgPulse = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.3)}50%{box-shadow:0 0 0 10px rgba(239,68,68,0)}`;
const popIn = keyframes`0%{opacity:0;transform:scale(.86) translateY(14px)}100%{opacity:1;transform:scale(1) translateY(0)}`;
const scanAnim = keyframes`0%{transform:translateX(-200%)}100%{transform:translateX(600%)}`;
const dotPulse = keyframes`0%,100%{transform:scale(1);opacity:.4}50%{transform:scale(1.5);opacity:1}`;

// ── helpers ──
const toFrac = (n) => {
  try {
    return new Fraction(n).toFraction(true);
  } catch {
    return String(n);
  }
};
const renderContent = (text) => {
  if (!text) return null;
  return text
    .split(/(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g)
    .map((p, i) => {
      if (p.startsWith("$$") && p.endsWith("$$"))
        return (
          <div key={i} style={{ margin: "8px 0" }}>
            <BlockMath math={p.slice(2, -2)} />
          </div>
        );
      if (p.startsWith("\\[") && p.endsWith("\\]"))
        return (
          <div key={i} style={{ margin: "8px 0" }}>
            <BlockMath math={p.slice(2, -2)} />
          </div>
        );
      if (p.startsWith("\\(") && p.endsWith("\\)"))
        return <InlineMath key={i} math={p.slice(2, -2)} />;
      if (p.startsWith("$") && p.endsWith("$"))
        return <InlineMath key={i} math={p.slice(1, -1)} />;
      return (
        <span key={i} style={{ whiteSpace: "pre-line" }}>
          {p}
        </span>
      );
    });
};

// ── styled ──
const Root = styled(Box)({
  height: "100vh",
  background: "#f0f3f9",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'Sora',sans-serif",
  overflow: "hidden",
});

const TopBar = styled(Box)({
  height: 62,
  background: "#07101f",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 20px",
  flexShrink: 0,
  zIndex: 200,
  borderBottom: "1px solid rgba(255,255,255,.07)",
  boxShadow: "0 4px 28px rgba(0,0,0,.28)",
});

const TimerChip = styled(Box)(({ urgent }) => ({
  display: "flex",
  alignItems: "center",
  gap: 7,
  background:
    urgent === "true" ? "rgba(239,68,68,.15)" : "rgba(255,255,255,.08)",
  border: `1.5px solid ${
    urgent === "true" ? "rgba(239,68,68,.45)" : "rgba(255,255,255,.1)"
  }`,
  borderRadius: 10,
  padding: "5px 14px",
  animation:
    urgent === "true" ? `${urgPulse} 1.4s ease-in-out infinite` : "none",
}));

const CalcSide = styled(Box)(({ open }) => ({
  width: open === "true" ? 284 : 0,
  minWidth: open === "true" ? 284 : 0,
  background: "#0d1829",
  borderRight: "1px solid rgba(255,255,255,.07)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  transition:
    "width .32s cubic-bezier(.22,1,.36,1), min-width .32s cubic-bezier(.22,1,.36,1)",
  flexShrink: 0,
}));

const CalcInner = styled(Box)({
  width: 284,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: "16px 13px",
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,.1)",
    borderRadius: 4,
  },
});

const CalcScreen = styled(Box)({
  background: "#060e1c",
  borderRadius: 12,
  padding: "14px 15px",
  marginBottom: 12,
  minHeight: 82,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  justifyContent: "flex-end",
  border: "1px solid rgba(255,255,255,.08)",
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    background:
      "linear-gradient(90deg,transparent,rgba(96,165,250,.25),transparent)",
    animation: `${scanAnim} 4s ease-in-out infinite`,
  },
});

const CBtn = styled(Button)(({ cv }) => ({
  borderRadius: 9,
  minWidth: 0,
  padding: "10px 3px",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".78rem",
  textTransform: "none",
  transition: "all .15s ease",
  ...(cv === "op" && {
    background: "#1e3a70",
    color: "#93c5fd",
    "&:hover": { background: "#1d4ed8", transform: "translateY(-1px)" },
  }),
  ...(cv === "fn" && {
    background: "#0c3344",
    color: "#67e8f9",
    fontSize: ".69rem",
    "&:hover": { background: "#0e4d66", transform: "translateY(-1px)" },
  }),
  ...(cv === "eq" && {
    background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
    color: "#fff",
    boxShadow: "0 6px 16px -4px rgba(37,99,235,.5)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 22px -4px rgba(37,99,235,.6)",
    },
  }),
  ...(cv === "cl" && {
    background: "#450a0a",
    color: "#fca5a5",
    "&:hover": { background: "#7f1d1d", transform: "translateY(-1px)" },
  }),
  ...(cv === "num" && {
    background: "#1e2d45",
    color: "#e2e8f0",
    "&:hover": { background: "#253553", transform: "translateY(-1px)" },
  }),
  ...(cv === "mod" && {
    background: "transparent",
    color: "#64748b",
    border: "1px solid rgba(255,255,255,.1)",
    fontSize: ".67rem",
    "&:hover": { background: "rgba(255,255,255,.06)" },
  }),
  ...(cv === "ac" && {
    background: "#7f1d1d",
    color: "#fca5a5",
    fontSize: ".82rem",
    "&:hover": { background: "#991b1b", transform: "translateY(-1px)" },
  }),
}));

const Body = styled(Box)({
  display: "flex",
  flex: 1,
  overflow: "hidden",
  minHeight: 0,
});
const MainArea = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minHeight: 0,
});

const ScrollPane = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "22px 28px",
  minHeight: 0,
  "&::-webkit-scrollbar": { width: 5 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(15,23,42,.13)",
    borderRadius: 6,
  },
  "&::-webkit-scrollbar-thumb:hover": { background: "rgba(15,23,42,.22)" },
});

const QCard = styled(Box)({
  background: "#fff",
  borderRadius: 18,
  border: "1.5px solid #e8edf5",
  padding: "28px 32px 24px",
  boxShadow: "0 4px 24px -8px rgba(15,23,42,.08)",
  animation: `${fadeUp} .3s cubic-bezier(.22,1,.36,1) both`,
  marginBottom: 16,
});

const StyledTextarea = styled(TextField)({
  "& .MuiInputBase-root": {
    borderRadius: 13,
    fontFamily: "'Sora',sans-serif",
    fontSize: ".93rem",
    background: "#fafbff",
    color: "#0f172a",
    lineHeight: 1.75,
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#e8edf5", borderWidth: 1.5 },
    "&:hover fieldset": { borderColor: "#93c5fd" },
    "&.Mui-focused fieldset": {
      borderColor: "#3b82f6",
      borderWidth: 2,
      boxShadow: "0 0 0 4px rgba(59,130,246,.09)",
    },
  },
  "& .MuiInputBase-root.Mui-focused": { background: "#fff" },
});

const NavDot = styled(Box)(({ iscurrent, isanswered }) => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".76rem",
  cursor: "pointer",
  transition: "all .18s ease",
  flexShrink: 0,
  border:
    iscurrent === "true"
      ? "2.5px solid #3b82f6"
      : isanswered === "true"
      ? "2px solid #22c55e"
      : "2px solid #e2e8f0",
  background:
    iscurrent === "true"
      ? "#dbeafe"
      : isanswered === "true"
      ? "#f0fdf4"
      : "#fff",
  color:
    iscurrent === "true"
      ? "#1d4ed8"
      : isanswered === "true"
      ? "#15803d"
      : "#64748b",
  "&:hover": {
    transform: "scale(1.1)",
    boxShadow: "0 2px 8px rgba(59,130,246,.18)",
  },
}));

const BottomNav = styled(Box)({
  background: "#fff",
  borderTop: "1.5px solid #e8edf5",
  padding: "12px 24px 14px",
  flexShrink: 0,
  boxShadow: "0 -4px 18px -4px rgba(15,23,42,.07)",
});

// ── component ──
export default function WaecExamTheoryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subjectId = searchParams.get("subjectId");

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [examYear, setExamYear] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [answersShown, setAnswersShown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [previewImg, setPreviewImg] = useState(null);

  // calculator
  const [calcOpen, setCalcOpen] = useState(false);
  const [disp, setDisp] = useState("");
  const [expr, setExpr] = useState("");
  const [ans, setAns] = useState(0);
  const [hist, setHist] = useState([]);
  const [showHist, setShowHist] = useState(false);
  const [angMode, setAngMode] = useState("DEG");

  const token = JSON.parse(localStorage.getItem("authToken") || "{}");
  const submitRef = useRef(false);

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Serif+Display:ital@0;1&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
     const API_URL = getApiUrl();
    if (!token?.access || !subjectId) {
      navigate("/student-login");
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/theory-questions/${subjectId}/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        });
        const d = await r.json();
        setQuestions(d.questions || []);
        setExamYear(d.year || null);
        const def = {};
        (d.questions || []).forEach((_, i) => {
          def[i] = "";
        });
        setAnswers(def);
        if (d.theory_time) {
          const [h, m, s] = d.theory_time.split(":").map(Number);
          setTimeLeft(h * 3600 + m * 60 + s);
        } else setTimeLeft(7200);
      } catch {
        toast.error("Failed to load theory questions.");
        navigate("/waec-subject-page");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [subjectId]);

  const submitExam = useCallback(() => {
    if (submitRef.current) return;
    submitRef.current = true;
    setShowPopup(true);
  }, []);

  useEffect(() => {
    if (timeLeft === null || loading) return;
    const id = setInterval(
      () =>
        setTimeLeft((p) => {
          if (p <= 1) {
            clearInterval(id);
            submitExam();
            return 0;
          }
          return p - 1;
        }),
      1000
    );
    return () => clearInterval(id);
  }, [timeLeft, loading, submitExam]);

  const h = timeLeft !== null ? Math.floor(timeLeft / 3600) : 0;
  const m = timeLeft !== null ? Math.floor((timeLeft % 3600) / 60) : 0;
  const s = timeLeft !== null ? timeLeft % 60 : 0;
  const timeStr = `${String(h).padStart(2, "0")}:${String(m).padStart(
    2,
    "0"
  )}:${String(s).padStart(2, "0")}`;
  const isUrgent = timeLeft !== null && timeLeft <= 300;
  const isWarn = timeLeft !== null && timeLeft <= 600 && !isUrgent;

  // calc
  const append = (v) => setDisp((p) => p + v);
  const clearD = () => {
    setDisp("");
    setExpr("");
  };
  const delLast = () => setDisp((p) => p.slice(0, -1));
  const calc = useCallback(() => {
    try {
      let e = disp.replace(/ANS/g, String(ans));
      if (angMode === "DEG")
        e = e
          .replace(/sin\(([^)]*)\)/g, "sin(($1)*pi/180)")
          .replace(/cos\(([^)]*)\)/g, "cos(($1)*pi/180)")
          .replace(/tan\(([^)]*)\)/g, "tan(($1)*pi/180)");
      const res = evaluate(e);
      const frac = toFrac(res);
      setAns(res);
      setExpr(disp + " =");
      setDisp(frac);
      setHist((p) => [...p, `${disp} = ${frac}`].slice(-10));
    } catch {
      setDisp("Error");
      setExpr("");
    }
  }, [disp, ans, angMode]);

  useEffect(() => {
    const hk = (e) => {
      if (!calcOpen) return;
      if ("0123456789+-*/().".includes(e.key)) append(e.key);
      if (e.key === "Enter") calc();
      if (e.key === "Backspace") delLast();
    };
    window.addEventListener("keydown", hk);
    return () => window.removeEventListener("keydown", hk);
  }, [calc, calcOpen]);

  const calcRows = [
    [
      { l: "sin", a: () => append("sin("), v: "fn" },
      { l: "cos", a: () => append("cos("), v: "fn" },
      { l: "tan", a: () => append("tan("), v: "fn" },
      { l: "log", a: () => append("log("), v: "fn" },
    ],
    [
      { l: "√", a: () => append("sqrt("), v: "fn" },
      { l: "x²", a: () => append("^2"), v: "fn" },
      { l: "xʸ", a: () => append("^"), v: "fn" },
      { l: "x!", a: () => append("!"), v: "fn" },
    ],
    [
      { l: "π", a: () => append("pi"), v: "fn" },
      { l: "e", a: () => append("e"), v: "fn" },
      { l: "ANS", a: () => append("ANS"), v: "fn" },
      { l: "( )", a: () => append("("), v: "fn" },
    ],
    [
      { l: "7", a: () => append("7"), v: "num" },
      { l: "8", a: () => append("8"), v: "num" },
      { l: "9", a: () => append("9"), v: "num" },
      { l: "÷", a: () => append("/"), v: "op" },
    ],
    [
      { l: "4", a: () => append("4"), v: "num" },
      { l: "5", a: () => append("5"), v: "num" },
      { l: "6", a: () => append("6"), v: "num" },
      { l: "×", a: () => append("*"), v: "op" },
    ],
    [
      { l: "1", a: () => append("1"), v: "num" },
      { l: "2", a: () => append("2"), v: "num" },
      { l: "3", a: () => append("3"), v: "num" },
      { l: "−", a: () => append("-"), v: "op" },
    ],
    [
      { l: "0", a: () => append("0"), v: "num" },
      { l: ".", a: () => append("."), v: "num" },
      { l: "⌫", a: delLast, v: "cl" },
      { l: "+", a: () => append("+"), v: "op" },
    ],
  ];

  const currentQ = questions[currentIdx];
  const totalQ = questions.length;
  const answeredCount = Object.values(answers).filter(
    (v) => v && v.trim().length > 0
  ).length;

  if (loading)
    return (
      <Root
        sx={{
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          background: "#07101f",
        }}
      >
        <CircularProgress size={48} thickness={4} sx={{ color: "#3b82f6" }} />
        <Typography
          sx={{
            fontFamily: "'Sora',sans-serif",
            color: "rgba(255,255,255,.5)",
            fontSize: ".9rem",
          }}
        >
          Loading theory exam…
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#3b82f6",
                animation: `${dotPulse} 1.4s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </Box>
      </Root>
    );

  return (
    <Root>
      {/* TOP BAR */}
      <TopBar>
        {/* Left */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            onClick={() => navigate(-1)}
            sx={{
              width: 34,
              height: 34,
              borderRadius: "9px",
              background: "rgba(255,255,255,.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { background: "rgba(255,255,255,.15)" },
              transition: "all .2s",
            }}
          >
            <ArrowBackIcon
              sx={{ color: "rgba(255,255,255,.6)", fontSize: 17 }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                background: "linear-gradient(135deg,#6d28d9,#a78bfa)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AutoStoriesIcon sx={{ color: "#fff", fontSize: 16 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                color: "#e2e8f0",
                fontSize: ".86rem",
                display: { xs: "none", sm: "block" },
              }}
            >
              Theory Exam {examYear && `· ${examYear}`}
            </Typography>
          </Box>
          <Tooltip
            title={calcOpen ? "Close Calculator" : "Open Calculator"}
            arrow
          >
            <Box
              onClick={() => setCalcOpen((o) => !o)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "9px",
                background: calcOpen
                  ? "linear-gradient(135deg,#1d4ed8,#3b82f6)"
                  : "rgba(255,255,255,.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all .22s",
                boxShadow: calcOpen
                  ? "0 4px 14px -4px rgba(37,99,235,.5)"
                  : "none",
                "&:hover": {
                  background: calcOpen
                    ? "linear-gradient(135deg,#1e40af,#2563eb)"
                    : "rgba(255,255,255,.15)",
                },
              }}
            >
              <CalculateIcon
                sx={{
                  fontSize: 18,
                  color: calcOpen ? "#fff" : "rgba(255,255,255,.6)",
                }}
              />
            </Box>
          </Tooltip>
        </Box>

        {/* Center */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontWeight: 700,
              color: "#e2e8f0",
              fontSize: ".86rem",
              lineHeight: 1.2,
            }}
          >
            Q{currentIdx + 1} of {totalQ}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontSize: ".66rem",
              color: "rgba(255,255,255,.35)",
            }}
          >
            {answeredCount}/{totalQ} answered
          </Typography>
        </Box>

        {/* Right */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TimerChip urgent={isUrgent ? "true" : "false"}>
            <AccessTimeIcon
              sx={{
                fontSize: 15,
                color: isUrgent ? "#f87171" : isWarn ? "#fbbf24" : "#60a5fa",
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 800,
                fontSize: ".88rem",
                color: isUrgent ? "#f87171" : isWarn ? "#fbbf24" : "#e2e8f0",
                letterSpacing: ".04em",
              }}
            >
              {timeStr}
            </Typography>
          </TimerChip>
          <Button
            onClick={submitExam}
            endIcon={<SendIcon sx={{ fontSize: ".88rem !important" }} />}
            sx={{
              borderRadius: 9,
              textTransform: "none",
              fontFamily: "'Sora',sans-serif",
              fontWeight: 700,
              fontSize: ".78rem",
              py: "6px",
              px: 1.8,
              background: "linear-gradient(105deg,#15803d,#22c55e)",
              boxShadow: "0 6px 16px -4px rgba(22,163,74,.45)",
              "&:hover": { transform: "translateY(-2px)" },
              transition: "all .22s",
              color: "#fff",
            }}
            variant="contained"
          >
            Submit
          </Button>
        </Box>
      </TopBar>

      {/* BODY */}
      <Body>
        {/* CALCULATOR SIDEBAR */}
        <CalcSide open={calcOpen ? "true" : "false"}>
          {calcOpen && (
            <CalcInner
              sx={{
                animation: `${slideInL} .3s cubic-bezier(.22,1,.36,1) both`,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.8,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalculateIcon sx={{ color: "#60a5fa", fontSize: 16 }} />
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontWeight: 700,
                      color: "#e2e8f0",
                      fontSize: ".82rem",
                    }}
                  >
                    Calculator
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.6 }}>
                  {["DEG", "RAD"].map((mode) => (
                    <CBtn
                      key={mode}
                      cv="mod"
                      onClick={() => setAngMode(mode)}
                      sx={{
                        background:
                          angMode === mode ? "#1e3a70" : "transparent",
                        color: angMode === mode ? "#93c5fd" : "#475569",
                      }}
                    >
                      {mode}
                    </CBtn>
                  ))}
                </Box>
              </Box>

              {/* Display */}
              <CalcScreen>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".63rem",
                    color: "#334155",
                    mb: 0.3,
                    wordBreak: "break-all",
                    width: "100%",
                    textAlign: "right",
                  }}
                >
                  {expr || "\u00A0"}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 800,
                    fontSize: disp.length > 14 ? ".95rem" : "1.55rem",
                    color: disp === "Error" ? "#f87171" : "#e2e8f0",
                    wordBreak: "break-all",
                    textAlign: "right",
                    transition: "font-size .15s ease",
                  }}
                >
                  {disp || "0"}
                </Typography>
              </CalcScreen>

              {/* AC + brackets */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 0.7,
                  mb: 0.8,
                }}
              >
                <CBtn cv="ac" onClick={clearD} fullWidth>
                  AC
                </CBtn>
                <CBtn cv="fn" onClick={() => append("(")} fullWidth>
                  ( )
                </CBtn>
              </Box>

              {/* Function + number rows */}
              {calcRows.map((row, ri) => (
                <Box
                  key={ri}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4,1fr)",
                    gap: "5px",
                    mb: "5px",
                  }}
                >
                  {row.map((b, bi) => (
                    <CBtn key={bi} cv={b.v} onClick={b.a} fullWidth>
                      {b.l}
                    </CBtn>
                  ))}
                </Box>
              ))}

              {/* Equals */}
              <Box sx={{ mt: "6px", mb: 1.5 }}>
                <CBtn
                  cv="eq"
                  onClick={calc}
                  fullWidth
                  sx={{ py: 1.2, fontSize: "1rem" }}
                >
                  =
                </CBtn>
              </Box>

              {/* History */}
              <Box
                onClick={() => setShowHist((h) => !h)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  color: "#475569",
                  "&:hover": { color: "#64748b" },
                  mb: 0.6,
                }}
              >
                <HistoryIcon sx={{ fontSize: 14 }} />
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".67rem",
                    fontWeight: 600,
                  }}
                >
                  {showHist ? "Hide" : "Show"} History
                </Typography>
              </Box>
              {showHist && (
                <Box
                  sx={{
                    background: "#060e1c",
                    borderRadius: 9,
                    p: "8px 10px",
                    maxHeight: 110,
                    overflowY: "auto",
                  }}
                >
                  {hist.length > 0 ? (
                    hist
                      .slice()
                      .reverse()
                      .map((hh, i) => (
                        <Typography
                          key={i}
                          sx={{
                            fontFamily: "'Sora',sans-serif",
                            fontSize: ".65rem",
                            color: "#475569",
                            mb: 0.3,
                            borderBottom: "1px solid #0d1829",
                            pb: 0.3,
                          }}
                        >
                          {hh}
                        </Typography>
                      ))
                  ) : (
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontSize: ".65rem",
                        color: "#334155",
                      }}
                    >
                      No calculations yet
                    </Typography>
                  )}
                </Box>
              )}
            </CalcInner>
          )}
        </CalcSide>

        {/* MAIN */}
        <MainArea>
          <ScrollPane>
            {/* Section label */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".7rem",
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                }}
              >
                Theory · Question {currentIdx + 1} of {totalQ}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {answers[currentIdx]?.trim().length > 0 && (
                  <Chip
                    icon={
                      <CheckCircleIcon sx={{ fontSize: "13px !important" }} />
                    }
                    label="Answered"
                    size="small"
                    sx={{
                      background: "#f0fdf4",
                      color: "#16a34a",
                      border: "1px solid #bbf7d0",
                      fontFamily: "'Sora',sans-serif",
                      fontWeight: 700,
                      fontSize: ".64rem",
                    }}
                  />
                )}
              </Box>
            </Box>

            {currentQ && (
              <QCard key={`theory-${currentIdx}`}>
                {/* Q badge */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "11px",
                      background: "linear-gradient(135deg,#6d28d9,#8b5cf6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 6px 16px -4px rgba(109,40,217,.32)",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 800,
                        color: "#fff",
                        fontSize: ".85rem",
                      }}
                    >
                      {currentIdx + 1}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".64rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: ".07em",
                    }}
                  >
                    Theory Question
                  </Typography>
                </Box>

                {/* Question image */}
                {currentQ.quesImg && (
                  <Box
                    sx={{
                      mb: 3,
                      borderRadius: 13,
                      overflow: "hidden",
                      border: "1.5px solid #e2e8f0",
                      maxWidth: 500,
                      cursor: "zoom-in",
                    }}
                    onClick={() => setPreviewImg(currentQ.quesImg)}
                  >
                    <img
                      src={currentQ.quesImg}
                      alt="question"
                      style={{ width: "100%", display: "block" }}
                    />
                  </Box>
                )}

                {/* Question text */}
                <Box
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: "1.02rem",
                    color: "#0f172a",
                    lineHeight: 1.85,
                    mb: 3.5,
                    p: "16px 20px",
                    background: "#f8faff",
                    borderRadius: 12,
                    border: "1px solid #e8edf5",
                  }}
                >
                  {renderContent(currentQ.question)}
                </Box>

                {/* Answer label */}
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".72rem",
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                    mb: 1,
                  }}
                >
                  Your Answer
                </Typography>

                {/* Textarea */}
                <StyledTextarea
                  fullWidth
                  multiline
                  rows={10}
                  placeholder="Write your detailed answer here. Be structured and thorough in your response…"
                  value={answers[currentIdx] || ""}
                  onChange={(e) =>
                    setAnswers((p) => ({ ...p, [currentIdx]: e.target.value }))
                  }
                  variant="outlined"
                />

                {/* Char count */}
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".68rem",
                      color: "#cbd5e1",
                    }}
                  >
                    {(answers[currentIdx] || "").length} characters
                  </Typography>
                </Box>
              </QCard>
            )}

            <Box sx={{ height: 8 }} />
          </ScrollPane>

          {/* BOTTOM NAV */}
          <BottomNav>
            {/* Prev / Next */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Button
                onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                disabled={currentIdx === 0}
                startIcon={<ChevronLeftIcon />}
                sx={{
                  borderRadius: 9,
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 600,
                  fontSize: ".82rem",
                  color: "#64748b",
                  border: "1.5px solid #e2e8f0",
                  px: 2,
                  py: 0.85,
                  "&:hover": { background: "#f1f5f9", borderColor: "#cbd5e1" },
                  "&.Mui-disabled": { opacity: 0.3 },
                }}
              >
                Previous
              </Button>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".8rem",
                  fontWeight: 600,
                  color: "#94a3b8",
                }}
              >
                {currentIdx + 1}{" "}
                <Box component="span" sx={{ opacity: 0.45 }}>
                  / {totalQ}
                </Box>
              </Typography>
              <Button
                onClick={() =>
                  setCurrentIdx((i) => Math.min(totalQ - 1, i + 1))
                }
                disabled={currentIdx === totalQ - 1}
                endIcon={<ChevronRightIcon />}
                sx={{
                  borderRadius: 9,
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  fontSize: ".82rem",
                  color: "#fff",
                  background: "linear-gradient(105deg,#6d28d9,#8b5cf6)",
                  px: 2,
                  py: 0.85,
                  boxShadow: "0 5px 14px -4px rgba(109,40,217,.35)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 9px 20px -4px rgba(109,40,217,.45)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.3,
                    background: "#e2e8f0",
                    color: "#94a3b8",
                    boxShadow: "none",
                  },
                  transition: "all .22s",
                }}
              >
                Next
              </Button>
            </Box>

            {/* Number pad */}
            <Box
              sx={{
                background: "#f8faff",
                borderRadius: 12,
                border: "1px solid #eef2fb",
                p: "10px 14px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".62rem",
                    fontWeight: 700,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: ".09em",
                  }}
                >
                  Jump to question
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  {[
                    { color: "#8b5cf6", label: "Current" },
                    { color: "#22c55e", label: "Answered" },
                    { color: "#e2e8f0", label: "Pending" },
                  ].map((l) => (
                    <Box
                      key={l.label}
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: 3,
                          background: l.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: ".6rem",
                          color: "#94a3b8",
                        }}
                      >
                        {l.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  pt: "2px",
                }}
              >
                {questions.map((_, i) => (
                  <NavDot
                    key={i}
                    iscurrent={i === currentIdx ? "true" : "false"}
                    isanswered={
                      answers[i]?.trim().length > 0 ? "true" : "false"
                    }
                    onClick={() => setCurrentIdx(i)}
                  >
                    {i + 1}
                  </NavDot>
                ))}
              </Box>
            </Box>
          </BottomNav>
        </MainArea>
      </Body>

      {/* SUBMIT CONFIRMATION POPUP */}
      {showPopup && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(7,16,31,.82)",
            backdropFilter: "blur(12px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            p: 2,
          }}
        >
          <Box
            sx={{
              background: "#fff",
              borderRadius: "26px",
              p: "40px 38px",
              textAlign: "center",
              width: "100%",
              maxWidth: 410,
              boxShadow: "0 48px 96px -24px rgba(15,23,42,.35)",
              border: "1px solid #e2e8f0",
              animation: `${popIn} .4s cubic-bezier(.22,1,.36,1) both`,
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "20px",
                background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                border: "2px solid #bbf7d0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 22px",
                fontSize: "2rem",
              }}
            >
              📝
            </Box>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 800,
                fontSize: "1.25rem",
                color: "#0f172a",
                mb: 0.6,
              }}
            >
              Exam Complete!
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                color: "#64748b",
                fontSize: ".87rem",
                lineHeight: 1.65,
                mb: 0.8,
              }}
            >
              You answered <b style={{ color: "#16a34a" }}>{answeredCount}</b>{" "}
              of <b>{totalQ}</b> theory questions.
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                color: "#94a3b8",
                fontSize: ".8rem",
                mb: 3.5,
              }}
            >
              Would you like to view the model answers?
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
              <Button
                onClick={() => {
                  setShowPopup(false);
                  setAnswersShown(true);
                }}
                fullWidth
                variant="contained"
                sx={{
                  borderRadius: "11px",
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  py: 1.45,
                  fontSize: ".9rem",
                  background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
                  boxShadow: "0 8px 20px -6px rgba(37,99,235,.4)",
                  "&:hover": { transform: "translateY(-2px)" },
                  transition: "all .22s",
                }}
              >
                View Model Answers
              </Button>
              <Button
                onClick={() => navigate("/nsubjects")}
                fullWidth
                variant="outlined"
                sx={{
                  borderRadius: "11px",
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  py: 1.45,
                  fontSize: ".9rem",
                  color: "#22c55e",
                  borderColor: "#86efac",
                  borderWidth: 1.5,
                  "&:hover": { background: "#f0fdf4" },
                }}
              >
                Return to Dashboard
              </Button>
              <Button
                onClick={() => {
                  setShowPopup(false);
                  submitRef.current = false;
                }}
                fullWidth
                sx={{
                  borderRadius: "11px",
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 600,
                  py: 1.1,
                  fontSize: ".84rem",
                  color: "#94a3b8",
                  "&:hover": { background: "#f8faff" },
                }}
              >
                Keep Reviewing
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* MODEL ANSWERS OVERLAY */}
      {answersShown && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(7,16,31,.88)",
            backdropFilter: "blur(14px)",
            display: "flex",
            alignItems: "stretch",
            justifyContent: "center",
            zIndex: 10000,
            p: { xs: 0, md: 3 },
          }}
        >
          <Box
            sx={{
              background: "#fff",
              borderRadius: { xs: 0, md: "24px" },
              width: "100%",
              maxWidth: 760,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 48px 96px -24px rgba(15,23,42,.4)",
              animation: `${popIn} .4s cubic-bezier(.22,1,.36,1) both`,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: "#07101f",
                padding: "22px 28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
                borderBottom: "1px solid rgba(255,255,255,.07)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "9px",
                    background: "linear-gradient(135deg,#6d28d9,#8b5cf6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AutoStoriesIcon sx={{ color: "#fff", fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontWeight: 700,
                      color: "#e2e8f0",
                      fontSize: ".92rem",
                    }}
                  >
                    Model Answers
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".66rem",
                      color: "rgba(255,255,255,.38)",
                    }}
                  >
                    Theory Exam {examYear && `· ${examYear}`}
                  </Typography>
                </Box>
              </Box>
              <Button
                onClick={() => navigate("/nsubjects")}
                variant="contained"
                sx={{
                  borderRadius: 9,
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  fontSize: ".78rem",
                  py: "6px",
                  px: 2,
                  background: "linear-gradient(105deg,#15803d,#22c55e)",
                  boxShadow: "0 4px 14px -4px rgba(22,163,74,.45)",
                  "&:hover": { transform: "translateY(-2px)" },
                  transition: "all .22s",
                  color: "#fff",
                }}
              >
                Return to Dashboard
              </Button>
            </Box>

            {/* Scrollable answers */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                padding: "24px 28px",
                "&::-webkit-scrollbar": { width: 5 },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(15,23,42,.13)",
                  borderRadius: 6,
                },
              }}
            >
              {questions.map((q, i) => (
                <Box
                  key={i}
                  sx={{
                    mb: 4,
                    pb: 4,
                    borderBottom:
                      i < questions.length - 1 ? "1.5px solid #f1f5f9" : "none",
                  }}
                >
                  {/* Q number */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "9px",
                        background: "linear-gradient(135deg,#6d28d9,#8b5cf6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontWeight: 800,
                          color: "#fff",
                          fontSize: ".78rem",
                        }}
                      >
                        {i + 1}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 700,
                        fontSize: ".88rem",
                        color: "#0f172a",
                      }}
                    >
                      Question {i + 1}
                    </Typography>
                  </Box>

                  {/* Question */}
                  {q.quesImg && (
                    <Box
                      sx={{
                        mb: 2,
                        borderRadius: 11,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        maxWidth: 440,
                        cursor: "zoom-in",
                      }}
                      onClick={() => setPreviewImg(q.quesImg)}
                    >
                      <img
                        src={q.quesImg}
                        alt="q"
                        style={{ width: "100%", display: "block" }}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".95rem",
                      color: "#334155",
                      lineHeight: 1.78,
                      mb: 2.5,
                      p: "14px 18px",
                      background: "#f8faff",
                      borderRadius: 11,
                      border: "1px solid #e8edf5",
                    }}
                  >
                    {renderContent(q.question)}
                  </Box>

                  {/* Your answer */}
                  {answers[i]?.trim().length > 0 && (
                    <Box
                      sx={{
                        mb: 2,
                        p: "14px 18px",
                        background: "#f8faff",
                        borderRadius: 11,
                        border: "1.5px solid #dbeafe",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: ".68rem",
                          fontWeight: 700,
                          color: "#3b82f6",
                          textTransform: "uppercase",
                          letterSpacing: ".07em",
                          mb: 0.7,
                        }}
                      >
                        Your Answer
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: ".88rem",
                          color: "#1e293b",
                          lineHeight: 1.7,
                          whiteSpace: "pre-line",
                        }}
                      >
                        {answers[i]}
                      </Typography>
                    </Box>
                  )}

                  {/* Model answer */}
                  <Box
                    sx={{
                      p: "14px 18px",
                      background: "#f0fdf4",
                      borderRadius: 11,
                      border: "1.5px solid #86efac",
                      borderLeft: "5px solid #22c55e",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontSize: ".68rem",
                        fontWeight: 700,
                        color: "#16a34a",
                        textTransform: "uppercase",
                        letterSpacing: ".07em",
                        mb: 0.7,
                      }}
                    >
                      Model Answer
                    </Typography>
                    <Box
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontSize: ".92rem",
                        color: "#14532d",
                        lineHeight: 1.78,
                      }}
                    >
                      {renderContent(
                        q.answer ||
                          "No model answer provided for this question."
                      )}
                    </Box>
                  </Box>

                  {/* Answer image */}
                  {q.ansImg && (
                    <Box
                      sx={{
                        mt: 2,
                        borderRadius: 11,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        maxWidth: 440,
                        cursor: "zoom-in",
                      }}
                      onClick={() => setPreviewImg(q.ansImg)}
                    >
                      <img
                        src={q.ansImg}
                        alt="answer"
                        style={{ width: "100%", display: "block" }}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                background: "#f8faff",
                borderTop: "1.5px solid #e8edf5",
                p: "14px 28px",
                display: "flex",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Button
                onClick={() => navigate("/waec-subject-page")}
                variant="contained"
                sx={{
                  borderRadius: 11,
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  py: 1.35,
                  px: 5,
                  fontSize: ".9rem",
                  background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
                  boxShadow: "0 8px 20px -6px rgba(37,99,235,.4)",
                  "&:hover": { transform: "translateY(-2px)" },
                  transition: "all .22s",
                }}
              >
                Return to Dashboard
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* IMAGE PREVIEW */}
      {previewImg && (
        <Box
          onClick={() => setPreviewImg(null)}
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(7,16,31,.92)",
            backdropFilter: "blur(14px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10001,
            cursor: "zoom-out",
            p: 3,
          }}
        >
          <Box
            component="img"
            src={previewImg}
            alt="preview"
            sx={{
              maxWidth: "90%",
              maxHeight: "86vh",
              borderRadius: "14px",
              boxShadow: "0 32px 80px rgba(0,0,0,.6)",
              objectFit: "contain",
            }}
          />
        </Box>
      )}
    </Root>
  );
}
